import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { useAuthStore } from '../../store/authStore'
import { tokenStorage } from '../../lib/auth'
import { api } from '../../lib/api'
import type { User } from '../../types/user'
import Spinner from '../../components/ui/Spinner'

interface AuthContextValue {
  isInitialized: boolean
}

const AuthContext = createContext<AuthContextValue>({ isInitialized: false })

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:8501'

// 백엔드 access_token TTL 60분 — 50분마다 silent refresh 로 사용 중인 사용자가 만료로 끊기지 않게.
const REFRESH_INTERVAL_MS = 50 * 60 * 1000

/** silent refresh 시도. 반환값:
 *   - 'ok': refresh 성공
 *   - 'auth_failed': refresh token 자체가 만료/유효하지 않음 (401/403) → 진짜 로그아웃 필요
 *   - 'transient': 503/네트워크 오류 등 일시적 — 로그아웃 X, 재시도
 */
async function silentRefresh(): Promise<'ok' | 'auth_failed' | 'transient'> {
  // ⚠️ 절대 localStorage 에서 refresh 토큰을 찾지 마라.
  //    refresh 토큰은 **HttpOnly 쿠키(saife_refresh, path=/api/auth)** 로만 존재해
  //    JS 가 읽을 수 없다. 예전 코드는 `tokenStorage.getRefresh()` 가 항상 null 이라
  //    요청을 보내지도 못하고 곧바로 'auth_failed' 를 반환했고, 호출측이 그걸
  //    "refresh 토큰 만료"로 해석해 **14일짜리 세션이 살아 있는데도 로그아웃**시켰다.
  //    (2026-08-21 재현 확인: 앱 재진입 시 /auth/refresh 요청이 아예 안 나감)
  //    쿠키를 실어 보내려면 withCredentials 가 필수다 — api.ts 인터셉터와 같은 방식.
  try {
    const { data } = await axios.post<{ access_token: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    )
    tokenStorage.setAccess(data.access_token)
    return 'ok'
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 401 || status === 403) return 'auth_failed'
    return 'transient'  // 503/504/네트워크 — 토큰 유효, 백엔드 일시 장애
  }
}

// 앱 시작 시 저장된 토큰으로 사용자 정보를 복원하는 컨텍스트
export function AuthProvider({ children }: { children: ReactNode }) {
  const { setUser, logout } = useAuthStore()
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    async function init() {
      // 1. CSRF 쿠키 미리 발급 (POST/PATCH/DELETE 요청에 필요)
      try {
        await api.get('/auth/csrf')
      } catch (e) {
        console.warn('[Auth] CSRF 토큰 발급 실패 (계속 진행)', e)
      }

      // 2. 저장된 access token 으로 사용자 정보 복원 — 만료/위조 시 refresh 시도, 실패면 완전 로그아웃.
      const token = tokenStorage.getAccess()
      if (!token) {
        // persist 된 user 만 살아있고 토큰은 없는 stale 상태 — 정리.
        if (useAuthStore.getState().isAuthenticated) {
          logout()
        }
        setIsInitialized(true)
        return
      }
      try {
        const { data } = await api.get<User>('/auth/me')
        setUser(data)
      } catch (err) {
        // /auth/me 가 401/403 이면 진짜 토큰 만료 — refresh 시도 후 처리.
        // 503/네트워크 오류는 백엔드 일시 장애 — persist 된 user 그대로 두고 다음 요청에서 재시도.
        const status = (err as { response?: { status?: number } })?.response?.status
        const isAuthError = status === 401 || status === 403
        const isTransient = !status || status === 503 || status === 504 || status === 502 || status >= 500

        if (isTransient && !isAuthError) {
          // 백엔드 일시 장애 — 로그아웃 X. persist 된 user 유지.
          console.warn('[Auth] /auth/me transient error', status, '— keeping session, will retry on next request')
        } else {
          // 진짜 인증 오류 — refresh 시도
          const result = await silentRefresh()
          if (result === 'ok') {
            try {
              const { data } = await api.get<User>('/auth/me')
              setUser(data)
            } catch (e2) {
              const s2 = (e2 as { response?: { status?: number } })?.response?.status
              if (s2 === 401 || s2 === 403) {
                tokenStorage.clearAll()
                logout()
              }
            }
          } else if (result === 'auth_failed') {
            tokenStorage.clearAll()
            logout()
          }
          // result === 'transient' 일 땐 아무 것도 안 함 (다음 요청에서 자동 재시도)
        }
      } finally {
        setIsInitialized(true)
      }
    }
    void init()
  }, [setUser, logout])

  // 사용 중 세션 연장 — 50분마다 silent refresh 시도. 페이지 가시 상태일 때만.
  useEffect(() => {
    const tick = async () => {
      if (document.visibilityState !== 'visible') return
      // 같은 이유로 getRefresh() 가드는 쓸 수 없다(항상 null → 주기 연장이 한 번도
      // 실행되지 않았다). 로그인 상태의 판단 기준은 access 토큰 보유 여부다.
      if (!tokenStorage.getAccess()) return
      await silentRefresh()
    }
    const id = setInterval(tick, REFRESH_INTERVAL_MS)
    // 탭 다시 활성화 시 한번 — 백그라운드에서 만료된 경우 즉시 갱신
    const onVis = () => { if (document.visibilityState === 'visible') void tick() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  // 초기화 중에는 스피너 표시 (토큰 복원 중)
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-deep">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{ isInitialized }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  return useContext(AuthContext)
}
